import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { api, money } from "@/lib/api";
import { ASSETS, BRAND } from "@/lib/brand";
import { Reveal } from "@/components/Motion";
import { CheckCircle2, X, CalendarDays, ArrowRight, Phone, Tag } from "lucide-react";
import { WorkGallery } from "@/components/WorkGallery";
import { WORK, WORK_IS_SAMPLE } from "@/lib/work";
import { toast } from "sonner";

const WHY = [
  "Professional & reliable service",
  "Clean, organised installations",
  "Advanced Apex automation & programming",
  "Expert reef aquarium knowledge",
  "Residential & commercial service",
  "Honest advice & quality workmanship",
];

function toPascal(name) {
  return name.split("-").map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join("");
}
function Icon({ name, ...props }) {
  const C = Icons[toPascal(name || "wrench")] || Icons.Wrench;
  return <C {...props} />;
}

const today = new Date().toISOString().slice(0, 10);

export default function Services() {
  const [services, setServices] = useState([]);
  const [booking, setBooking] = useState(null); // service being booked
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", address: "", scheduled_date: "", notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/services").then((r) => setServices(r.data)); }, []);

  useEffect(() => {
    const id = window.location.hash.replace("#", "");
    if (id) {
      const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 400);
      return () => clearTimeout(t);
    }
  }, []);

  const open = (svc) => { setBooking(svc); setForm({ customer_name: "", customer_email: "", customer_phone: "", address: "", scheduled_date: "", notes: "" }); };

  const submit = async () => {
    if (!form.customer_name.trim()) return toast.error("Please enter your name");
    if (!form.scheduled_date) return toast.error("Please choose a service date");
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", { ...form, service_id: booking.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start booking");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* HERO with banner */}
      <section className="relative h-[62vh] min-h-[440px] overflow-hidden flex items-end">
        <img src={ASSETS.banner} alt="Exotic Reefing Inc services" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-void via-void/40 to-void/10" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pb-14 w-full">
          <Reveal>
            <span className="mono-label text-cyan">{BRAND.tagline}</span>
            <h1 className="font-display text-5xl md:text-7xl tracking-tight mt-4 max-w-3xl leading-[0.95]">Aquarium services & <span className="italic text-cyan">booking</span></h1>
            <p className="text-white/70 mt-5 max-w-xl">Book any service below and lock in your date with a small deposit. {BRAND.serving}</p>
            <div className="flex flex-wrap gap-4 mt-6">
              <a href={BRAND.phoneHref} className="btn-pill btn-solid inline-flex items-center gap-2" data-testid="services-call-btn"><Phone size={15} /> Call or text {BRAND.phone}</a>
              <Link to="/pricing" className="btn-pill inline-flex items-center gap-2" data-testid="services-pricing-btn"><Tag size={15} /> View full pricing</Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section id="book" className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 scroll-mt-24">
        <Reveal><h2 className="font-display text-4xl md:text-5xl mb-3">Aquarium Maintenance & Reef Services in South Florida</h2>
          <p className="text-white/50 mb-12 max-w-2xl">
            Exotic Reefing Inc offers professional <b className="text-white/70">aquarium maintenance, reef tank setup, water testing,
            Neptune Apex installation & programming, lighting installation</b> and coral care for residential and commercial clients
            across South Florida. Choose a service below to book — deposits are applied to your final invoice.
          </p></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => (
            <motion.div key={s.id} data-testid={`service-card-${s.id}`}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.06 }}
              className="glass rounded-2xl p-6 border border-white/10 card-glow flex flex-col">
              <div className="w-12 h-12 rounded-xl bg-cyan/10 border border-cyan/20 flex items-center justify-center mb-5">
                <Icon name={s.icon} className="text-cyan" size={22} />
              </div>
              <h3 className="font-display text-xl leading-tight mb-2">{s.name}</h3>
              <p className="text-white/50 text-sm mb-5 flex-1">{s.description}</p>
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <div>
                  <p className="mono-label text-white/40">Deposit</p>
                  <p className="font-mono text-cyan text-lg">{money(s.deposit)}</p>
                </div>
                <button onClick={() => open(s)} data-testid={`book-service-${s.id}`} className="btn-pill !py-2.5 !px-5 flex items-center gap-2">
                  <CalendarDays size={15} /> Book
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* OUR WORK GALLERY */}
      {WORK.length > 0 && (
        <section className="border-t border-white/10">
          <div id="gallery" className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 scroll-mt-24" data-testid="our-work-section">
            <Reveal>
              <span className="mono-label text-cyan">Our work</span>
              <h2 className="font-display text-4xl md:text-5xl mt-4 mb-3">Tanks we've maintained & installed</h2>
              <p className="text-white/50 mb-4 max-w-2xl">
                A look at real reef systems, equipment installs and Apex builds we've handled across South Florida.
                Tap any photo to view it larger.
              </p>
              {WORK_IS_SAMPLE && (
                <p className="text-toxic/90 text-xs font-mono mb-10 border border-toxic/30 rounded-lg px-3 py-2 inline-block" data-testid="work-sample-note">
                  Sample layout — these placeholders will be replaced with your real job photos.
                </p>
              )}
            </Reveal>
            <WorkGallery items={WORK} />
            <div className="mt-14 flex flex-col items-center text-center gap-4">
              <p className="text-white/60 max-w-lg">Like what you see? Let's build or maintain a reef like these for you.</p>
              <button
                onClick={() => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="book-maintenance-cta"
                className="btn-pill btn-solid inline-flex items-center gap-2 text-base !px-8 !py-3.5"
              >
                <CalendarDays size={18} /> Book a Maintenance Visit
              </button>
            </div>
          </div>
        </section>
      )}

      {/* WHY CHOOSE */}
      <section className="border-t border-white/10 bg-white/[0.015]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 grid md:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="mono-label text-cyan">Why choose us</span>
            <h2 className="font-display text-4xl md:text-5xl mt-4 mb-8 leading-tight">{BRAND.slogan}</h2>
            <ul className="space-y-4">
              {WHY.map((w) => (
                <li key={w} className="flex items-start gap-3 text-white/70"><CheckCircle2 className="text-cyan shrink-0 mt-0.5" size={20} /> {w}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <img src={ASSETS.flyer} alt="Exotic Reefing services" className="rounded-2xl border border-white/10 w-full max-w-sm mx-auto" style={{ boxShadow: "0 30px 90px -30px rgba(0,240,255,0.35)" }} />
          </Reveal>
        </div>
      </section>

      {/* BOOKING MODAL */}
      {booking && (
        <div className="fixed inset-0 z-[100] bg-void/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-5">
          <div className="glass rounded-2xl p-8 w-full max-w-lg my-10 border border-white/10" data-testid="booking-modal">
            <div className="flex justify-between items-start mb-6">
              <div>
                <span className="mono-label text-cyan">Book service</span>
                <h3 className="font-display text-2xl">{booking.name}</h3>
                <p className="text-white/50 text-sm mt-1">Deposit due now: <span className="text-cyan font-mono">{money(booking.deposit)}</span></p>
              </div>
              <button onClick={() => setBooking(null)} className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="mono-label text-white/40 block mb-2">Your name *</label>
                <input className="er-input" data-testid="booking-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
              </div>
              <div>
                <label className="mono-label text-white/40 block mb-2">Preferred date *</label>
                <input type="date" min={today} className="er-input" data-testid="booking-date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="mono-label text-white/40 block mb-2">Email</label>
                  <input type="email" className="er-input" data-testid="booking-email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} /></div>
                <div><label className="mono-label text-white/40 block mb-2">Phone</label>
                  <input className="er-input" data-testid="booking-phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} /></div>
              </div>
              <div><label className="mono-label text-white/40 block mb-2">Service address</label>
                <input className="er-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div><label className="mono-label text-white/40 block mb-2">Tank details / notes</label>
                <textarea rows={3} className="er-input" placeholder="e.g. 120g mixed reef, Apex, need dosing dialed in" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>

            <button onClick={submit} disabled={busy} data-testid="booking-pay-btn" className="btn-pill btn-solid w-full mt-6 flex items-center justify-center gap-2 disabled:opacity-50">
              {busy ? "Redirecting…" : <>Pay {money(booking.deposit)} deposit & book <ArrowRight size={15} /></>}
            </button>
            <p className="text-white/30 text-xs mt-3 text-center font-mono">Secured by Stripe · deposit applied to final invoice</p>
          </div>
        </div>
      )}
    </div>
  );
}
